import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { LoginRequest } from '../types/api';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import LanguageSelector from './LanguageSelector';
import Turnstile from './Turnstile';
import './Login.css';

// Import background images
import reforged1024 from '../assets/reforged-1024x330.png';

type AuthView = 'login' | 'register' | 'forgot-password';

const Login: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<AuthView>('login');
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isEmailVerificationError, setIsEmailVerificationError] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  
  const { login } = useAuth();

  // Check for success message from email confirmation
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      
      // Clear the location state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
      
      // Clear the message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Auto-clear error message after 3 seconds (except email verification errors)
  useEffect(() => {
    if (error && !isEmailVerificationError) {
      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      // Set new timeout - 3 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setError('');
      }, 3000); // 3 seconds
      
      return () => {
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }
      };
    }
  }, [error, isEmailVerificationError]);

  const dismissError = () => {
    setError('');
    setIsEmailVerificationError(false);
    setResendMessage('');
    setResendSuccess(false);
    setShowTurnstile(false);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  // Helper function to map error codes to translated messages
  const getErrorMessageFromCode = (errorCode: string): string => {
    const isEmailError = errorCode === '2';
    setIsEmailVerificationError(isEmailError);
    
    switch (errorCode) {
      case '1':
        return t('loginFailedInvalidCredentials');
      case '2':
        return t('loginFailedEmailNotConfirmed');
      case '3':
        return t('loginFailedUserNotActive');
      case '4':
        return t('loginFailedNoActiveSubscription');
      default:
        return t('emailConfirmationRequired');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Trim whitespace and tabs from email field
    const processedValue = name === 'email' ? value.trim() : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }));
    // No longer clearing error on input change - let it persist for the full 10 seconds
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setIsEmailVerificationError(false);
    setResendMessage('');
    setResendSuccess(false);
    setShowTurnstile(false);

    try {
      // Ensure email is trimmed before sending to API
      const trimmedFormData = {
        ...formData,
        email: formData.email.trim()
      };
      
      const response = await apiService.login(trimmedFormData);
      
      if (response.success && response.data) {
        // Clear appointment-related localStorage data to prevent sharing between users
        localStorage.removeItem('bookAppointment_selectedDate');
        login(response.data);
      } else {
        // Handle error response with specific error codes
        if (response.errors && response.errors.length > 0) {
          // Use the first error code to determine the message
          const errorCode = response.errors[0];
          setError(getErrorMessageFromCode(errorCode));
        } else {
          // Fallback to generic message or response message
          setError(response.message || t('emailConfirmationRequired'));
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Check if the error response has error codes
      if (err.response?.data?.errors && err.response.data.errors.length > 0) {
        const errorCode = err.response.data.errors[0];
        setError(getErrorMessageFromCode(errorCode));
      } else {
        const errorMessage = err.response?.data?.message || 
                            err.message || 
                            'An error occurred during login';
        
        // Check for email confirmation related errors (legacy compatibility)
        if (errorMessage.toLowerCase().includes('email not confirmed') || 
            errorMessage.toLowerCase().includes('confirm your account')) {
          setError(t('loginFailedEmailNotConfirmed'));
          setIsEmailVerificationError(true);
        } else {
          // Use translated generic error message instead of raw error
          setError(t('loginFailedGeneric'));
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = formData.email && formData.password.length >= 6;

  // Handle resend verification email
  const handleResendVerificationEmail = async () => {
    if (!formData.email.trim()) {
      return;
    }

    setIsResending(true);
    setResendMessage('');
    setResendSuccess(false);

    try {
      const response = await apiService.resendVerificationEmail({
        email: formData.email.trim(),
        turnstileToken: turnstileToken || undefined
      });

      if (response.success) {
        setResendSuccess(true);
        setResendMessage(t('verificationEmailResent'));
        setShowTurnstile(false);
        setTurnstileToken(null);
      } else {
        setResendMessage(response.message || t('failedToResendEmail'));
      }
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      
      // Handle specific error codes based on backend specification
      if (error.response?.data?.errors) {
        const errorCodes = error.response.data.errors;
        if (errorCodes.includes('USER_NOT_FOUND')) {
          setResendMessage(t('noAccountFoundWithEmail'));
        } else if (errorCodes.includes('EMAIL_ALREADY_CONFIRMED')) {
          setResendMessage(t('emailAlreadyVerifiedCanLogin'));
        } else if (errorCodes.includes('ACCOUNT_INACTIVE')) {
          setResendMessage(t('accountDisabledContactSupport'));
        } else if (errorCodes.includes('SECURITY_VERIFICATION_FAILED')) {
          setResendMessage(t('securityCheckFailedTryAgain'));
          setShowTurnstile(true);
        } else if (errorCodes.includes('VALIDATION_ERROR')) {
          setResendMessage(t('invalidInputValidation'));
        } else if (errorCodes.includes('SERVER_ERROR')) {
          setResendMessage(t('somethingWentWrongTryAgainLater'));
        } else {
          setResendMessage(error.response?.data?.message || t('failedToResendEmail'));
        }
      } else if (error.response?.status === 429) {
        // Rate limit error - show exact message from API
        setResendMessage(error.response?.data?.message || t('rateLimitExceeded'));
      } else {
        setResendMessage(error.response?.data?.message || t('failedToResendEmail'));
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    setShowTurnstile(false);
    // Automatically retry resend after getting turnstile token
    setTimeout(() => {
      handleResendVerificationEmail();
    }, 100);
  };

  // Handle view switching
  const handleShowRegister = () => {
    setCurrentView('register');
    setError('');
    setIsEmailVerificationError(false);
    setResendMessage('');
    setResendSuccess(false);
    setShowTurnstile(false);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  const handleShowForgotPassword = () => {
    setCurrentView('forgot-password');
    setError('');
    setIsEmailVerificationError(false);
    setResendMessage('');
    setResendSuccess(false);
    setShowTurnstile(false);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  const handleBackToLogin = () => {
    setCurrentView('login');
    setError('');
    setIsEmailVerificationError(false);
    setResendMessage('');
    setResendSuccess(false);
    setShowTurnstile(false);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  // Render different views
  if (currentView === 'register') {
    return <Register onBackToLogin={handleBackToLogin} />;
  }

  if (currentView === 'forgot-password') {
    return <ForgotPassword onBackToLogin={handleBackToLogin} />;
  }

  return (
    <div className="login-container">
      <div className="login-logo">
        <img src={reforged1024} alt="Reforged Training Zone" className="logo-image" />
      </div>
      <div className="login-card">
        <div className="login-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span></span>
            <LanguageSelector />
          </div>
          <p>{t('pleaseSignInToContinue')}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder={t('email')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              disabled={isLoading}
              placeholder={t('password')}
            />
          </div>
          
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
              <button 
                type="button" 
                className="error-dismiss-button"
                onClick={dismissError}
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {isEmailVerificationError && (
            <div className="resend-verification-section">
              {resendMessage && (
                <div className={`resend-message ${resendSuccess ? 'success' : 'error'}`}>
                  {resendMessage}
                </div>
              )}

              {showTurnstile && (
                <div className="turnstile-container">
                  <p>{t('pleaseCompleteCaptcha')}</p>
                  <Turnstile 
                    onVerify={handleTurnstileSuccess}
                    onError={() => setShowTurnstile(false)}
                  />
                </div>
              )}

              {!resendSuccess && (
                <button
                  type="button"
                  onClick={handleResendVerificationEmail}
                  disabled={isResending || !formData.email.trim()}
                  className="resend-verification-button"
                >
                  {isResending ? t('sending') : t('resendVerificationEmail')}
                </button>
              )}
            </div>
          )}
          
          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="login-button"
          >
            {isLoading ? t('signingIn') : t('signIn')}
          </button>
        </form>
        
        <div className="login-footer">
          <div className="login-links">
            <button 
              type="button" 
              className="link-button" 
              onClick={handleShowRegister}
              disabled={isLoading}
            >
              {t('createAccount')}
            </button>
            <button 
              type="button" 
              className="link-button" 
              onClick={handleShowForgotPassword}
              disabled={isLoading}
            >
              {t('forgotPassword')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 